import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddCategory from "../modal/AddCategory";
import { useDeleteCategory, useGetAllCategory, useUpdateCategory, useUpdateSortOrderAll } from "../hooks/hooks";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from 'react-router-dom';

import DeleteModal from "../modal/DeleteModal";
import { showSuccessToast, showErrorToast } from './../components/common'
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import NoDataFound from "../components/Common/NoDataFound";
import { DeleteIcon, EditIcon, MenuCateIcon, NDCategorieI } from "../assets/icons/icon";
import Header from "../components/Common/Header";
import SearchControls from "../components/Common/SearchControls";
import { ButtonWithIcon } from "../components/Common/Buttons";
import Pagination from "../components/Common/Pagination";
import { usePagination } from "../hooks/usePagination";

const ORDER_FIELD_PRIORITY = ["order", "sortOrder", "sequence", "displayOrder", "position", "priority"];


const Category = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { data: categoryList, refetch, isLoading: IsLoading } = useGetAllCategory();
  const { mutate: deleteCategory } = useDeleteCategory();
  const { mutateAsync: updateCategoryMutation } = useUpdateCategory();
  const { mutateAsync: updateAllSortOrdersMutation } = useUpdateSortOrderAll();
  const { menuList, selectedCategory, IsShowSubCategoryBox } = useSelector(
    (state) => state.api
  );
  const navigate = useNavigate();

  const [isOpenDelete, setisOpenDelete] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isEditItem, setIsEditItem] = useState(undefined);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isDeleteCategory, setIsDeleteCategory] = useState(undefined);

  const [allCategoryList, setAllCategoryList] = useState(categoryList);
  const [searchText, setSearchText] = useState('');
  const [filteredCategoryList, setFilteredCategoryList] = useState(categoryList);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [isOrderSaving, setIsOrderSaving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const debouncedHandleSearch = useCallback(debounce(handleSearchChange, 1000), []);
  const allCategoryRef = useRef(allCategoryList);

  const orderFieldKey = useMemo(() => {
    if (!filteredCategoryList || !filteredCategoryList.length) {
      return "order";
    }

    const sample = filteredCategoryList.find((item) =>
      ORDER_FIELD_PRIORITY.some((key) => typeof item?.[key] !== "undefined" && item?.[key] !== null)
    );

    if (!sample) {
      return "order";
    }

    const matchingKey = ORDER_FIELD_PRIORITY.find((key) => typeof sample?.[key] !== "undefined" && sample?.[key] !== null);
    return matchingKey || "order";
  }, [filteredCategoryList]);

  useEffect(() => {
    if (categoryList) {
      setAllCategoryList(categoryList);
      setFilteredCategoryList(categoryList);
      allCategoryRef.current = categoryList
    }
  }, [categoryList]);

  const handleOpenPopup = () => {
    setPopupOpen(true);
  };

  const handleAddNavigate = () => {
    navigate('/category/add');
  };

  const handleClosePopup = async (isRefresh) => {
    setPopupOpen(false);
    setIsEdit(false);
    setIsEditItem(undefined);

    if (isRefresh === true) {
      refetch();
    }
  };

  const onDelete = (isDelete) => {
    if (isDelete) {
      handleDeleteCategory(isDeleteCategory);
    }
    setisOpenDelete(false)
  }

  const handleDeleteCategory = (item) => {
    deleteCategory(item, {
      onSuccess: async (res) => {
        refetch();
        showSuccessToast(res?.data?.message);
      },
      onError: (err) => {
        if (err.response.data?.isUsed) {
          showErrorToast(t('err_del_category'));
        } else {
          showErrorToast(err.response.data.message);
        }
      },
    });
  };

  const applyOrderMetadata = (list) => {
    if (!Array.isArray(list)) return [];

    return list.map((item, idx) => {
      if (!item) return item;
      const orderValue = idx + 1;
      const updatedItem = {
        ...item,
        order: orderValue,
      };

      if (orderFieldKey && orderFieldKey !== "order") {
        updatedItem[orderFieldKey] = orderValue;
      }

      return updatedItem;
    });
  };

  const handlePersistCategoryOrder = async (updatedList, previousList) => {
    const payload = updatedList.map((item, index) => ({
      id: item._id,
      sortOrder: index + 1
    }));

    if (!payload.length) return;

    setIsOrderSaving(true);
    try {

      // ONE API CALL
      await updateAllSortOrdersMutation(payload);
      // await Promise.all(payloads.map((payload) => updateCategoryMutation(payload)));
      showSuccessToast(t('category_order_updated'));
      refetch();
    } catch (error) {
      console.error("Failed to update category order", error);
      showErrorToast(error?.response?.data?.message || t('category_order_update_failed'));
      if (previousList) {
        setFilteredCategoryList(previousList);
        setAllCategoryList(previousList);
        allCategoryRef.current = previousList;
      }
    } finally {
      setIsOrderSaving(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result?.destination || isOrderSaving) return;

    const sourceGlobalIndex = startIndex + result.source.index;
    const destinationGlobalIndex = startIndex + result.destination.index;

    if (sourceGlobalIndex === destinationGlobalIndex) {
      return;
    }

    const previousList = filteredCategoryList ? [...filteredCategoryList] : [];
    const reorderedList = (() => {
      if (!Array.isArray(previousList) || !previousList.length) {
        return previousList;
      }

      const updated = [...previousList];
      const [removed] = updated.splice(sourceGlobalIndex, 1);
      if (!removed) {
        return previousList;
      }
      if (typeof destinationGlobalIndex === "number") {
        updated.splice(destinationGlobalIndex, 0, removed);
      }
      return updated;
    })();

    const listWithMeta = applyOrderMetadata(reorderedList);
    setFilteredCategoryList(listWithMeta);
    setAllCategoryList(listWithMeta);
    allCategoryRef.current = listWithMeta;

    await handlePersistCategoryOrder(listWithMeta, previousList);
  };

  function handleSearchChange(value) {
    if (!allCategoryRef.current || !Array.isArray(allCategoryRef.current)) {
      console.warn("allCategoryRef is not ready or not an array");
      return;
    }

    const searchValue = value.trim().toLowerCase();

    if (!searchValue) {
      setFilteredCategoryList(allCategoryRef.current);
      return;
    }

    const filtered = allCategoryRef.current.filter((category) => {
      const guj = category.categoryName?.toLowerCase() || "";
      const eng = category.categoryNameEn?.toLowerCase() || "";

      return guj.includes(searchValue) || eng.includes(searchValue);
    });

    setFilteredCategoryList(filtered);
    setCurrentPage(1);
  }


  // Pagination calculations
  const totalItems = filteredCategoryList?.length || 0;

  const {
    totalPages,
    startIndex,
    endIndex,
    pageNumbers,
    onPageChange,
    onItemsPerPageChange
  } = usePagination({
    totalItems,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage
  });

  const currentItems = filteredCategoryList?.slice(startIndex, endIndex) || [];

  const CategoryCard = ({ item, index }) => (
    <div className="bg-white rounded-xl shadow-card border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {item.categoryName}
          </h3>
          {item.categoryNameEn && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <span className="text-sm text-gray-600">{item.categoryNameEn}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} iconClass="text-green-600 mr-0" btnclass="p-[5px] hover:bg-green-50 rounded-md transition-all border border-green-300" onClick={(e) => {
            e.stopPropagation();
            setIsEdit(true);
            setIsEditItem(item);
            handleOpenPopup(true);
          }} />
          <ButtonWithIcon title="Delete" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0 text-red-500" btnclass="p-[5px] text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={(e) => {
            e.stopPropagation();
            setIsDeleteCategory(item);
            setisOpenDelete(true);
          }} />
        </div>
      </div>
    </div>
  );

  const TableRow = ({ item, index, dragHandleProps = {}, draggableProps = {}, innerRef, isDragging }) => {
    const { style, ...restDraggableProps } = draggableProps || {};

    return (
      <div
        ref={innerRef}
        {...restDraggableProps}
        style={style}
        className={`w-full flex py-4 font-medium text-sm text-gray-700 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200
          ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"} ${isDragging ? "shadow-lg ring-2 ring-primary/30 bg-white" : ""}`}
      >
        <div className="w-[20%] pl-6 flex items-center font-medium text-gray-500 gap-3">
          <button
            type="button"
            aria-label={t('drag_to_reorder')}
            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            {...dragHandleProps}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4M10 8h4M10 12h4M10 16h4M10 20h4" />
            </svg>
          </button>
          {startIndex + index + 1}
        </div>
        <div className="w-[45%] flex items-center pr-4">
          <div className="font-semibold text-gray-900 truncate" title={item.categoryName}>
            {item.categoryName}
            {item.categoryNameEn ? ` (${item.categoryNameEn})` : ""}
          </div>
        </div>
        <div className="w-[35%] flex items-center">
          <div className="flex items-center gap-2">
            <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} iconClass="text-green-600 mr-0" btnclass="p-2 hover:bg-green-50 rounded-md transition-all border border-green-300" onClick={(e) => {
              e.stopPropagation();
              setIsEdit(true);
              setIsEditItem(item);
              handleOpenPopup(true);
            }} />
            <ButtonWithIcon title="Delete" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0 text-red-500" btnclass="p-2 text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={(e) => {
              e.stopPropagation();
              setIsDeleteCategory(item);
              setisOpenDelete(true);
            }} />
          </div>
        </div>
      </div>
    );
  };

  // const Pagination = () => {
  //   if (totalPages <= 1) return null;

  //   return (
  //     <div className="flex items-center justify-between gap-4 px-6 py-4 bg-gray-50 border-t border-gray-200">
  //       <div className="flex items-center gap-4">
  //         <div className="flex items-center gap-2">
  //           <span className="text-sm text-gray-600">Show:</span>
  //           <select
  //             value={itemsPerPage}
  //             onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
  //             className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
  //           >
  //             <option value={5}>5</option>
  //             <option value={10}>10</option>
  //             <option value={20}>20</option>
  //             <option value={50}>50</option>
  //           </select>
  //           <span className="text-sm text-gray-600">entries</span>
  //         </div>

  //         <div className="text-sm text-gray-600">
  //           Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
  //         </div>
  //       </div>

  //       <div className="flex items-center gap-2">
  //         <button
  //           onClick={() => handlePageChange(currentPage - 1)}
  //           disabled={currentPage === 1}
  //           className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
  //         >
  //           Previous
  //         </button>

  //         <div className="flex items-center gap-1">
  //           {getPageNumbers().map((page, index) => (
  //             <button
  //               key={index}
  //               onClick={() => typeof page === 'number' && handlePageChange(page)}
  //               disabled={page === '...'}
  //               className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${page === currentPage
  //                 ? 'bg-primary text-white'
  //                 : page === '...'
  //                   ? 'text-gray-400 cursor-default'
  //                   : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
  //                 }`}
  //             >
  //               {page}
  //             </button>
  //           ))}
  //         </div>

  //         <button
  //           onClick={() => handlePageChange(currentPage + 1)}
  //           disabled={currentPage === totalPages}
  //           className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
  //         >
  //           Next
  //         </button>
  //       </div>
  //     </div>
  //   );
  // };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-6 py-8">
          {/* Modern Combined Header & Search Section - Compact */}
          <div className="mb-6">
            {/* Header with Background */}
            <div className="relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Decorative Background Elements */}
              <div className="absolute inset-0 bg-gray-50/30" />
              <div className="absolute -top-10 -right-10 w-20 h-20 bg-blue-100/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-10 -left-10 w-20 h-20 bg-purple-100/20 rounded-full blur-2xl" />

              <div className="relative z-10 p-6">
                <Header title={t('Category')} subTitle={t('manage_your_menu_category')} buttonText={t('btn_add_new_menu_category')} handleClick={handleOpenPopup} pageIcon={<MenuCateIcon />} className="mb-4" titleClass="text-2xl mb-0" />

                {/* Search and Controls */}
                <SearchControls
                  searchText={searchText}
                  onSearchChange={setSearchText}
                  onSearchDebounced={debouncedHandleSearch}
                  placeholder={t('search_MenuC_lbl')}
                  viewMode={viewMode}
                  onViewChange={setViewMode}
                  totalItems={totalItems}
                  className="bg-gray-50 mt-2 border border-gray-200 p-4 shadow-none mb-0"
                />
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="bg-white rounded-xl shadow-card overflow-hidden">
            {IsLoading ? (
              <>
                {viewMode === 'table' ? (
                  <>
                    {/* Table Header Skeleton */}
                    <div className="bg-gray-50 border-b border-gray-200">
                      <div className="flex py-4 px-6 text-sm font-semibold text-gray-700">
                        <div className="w-[20%]">#</div>
                        <div className="w-[45%]">{t('menuCategory_name')}</div>
                        <div className="w-[35%]">{t('Action')}</div>
                      </div>
                    </div>

                    {/* Table Body Skeleton */}
                    <div>
                      {Array.from({ length: 6 }, (_, index) => (
                        <div
                          key={index}
                          className={`w-full flex py-4 border-b border-gray-100 ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"
                            }`}
                        >
                          <div className="w-[20%] pl-6 flex items-center">
                            <Skeleton height={16} width={20} />
                          </div>
                          <div className="w-[45%] flex items-center pr-4">
                            <div className="flex-1">
                              <Skeleton height={18} width="80%" />
                              <Skeleton height={14} width="60%" className="mt-1" />
                            </div>
                          </div>
                          <div className="w-[35%] flex items-center">
                            <div className="flex items-center gap-2">
                              <Skeleton height={32} width={32} className="rounded-lg" />
                              <Skeleton height={32} width={32} className="rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  /* Grid View Skeleton */
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {Array.from({ length: 8 }, (_, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-card border border-gray-100 p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <Skeleton height={20} width="70%" className="mb-2" />
                              <div className="flex items-center gap-2">
                                <Skeleton height={16} width={16} className="rounded" />
                                <Skeleton height={14} width="50%" />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Skeleton height={32} width={32} className="rounded-lg" />
                              <Skeleton height={32} width={32} className="rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pagination Skeleton */}
                {/* <div className="flex items-center justify-between gap-4 px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Skeleton height={16} width={40} />
                      <Skeleton height={32} width={60} className="rounded-md" />
                      <Skeleton height={16} width={50} />
                    </div>
                    <Skeleton height={16} width={120} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton height={32} width={70} className="rounded-md" />
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }, (_, index) => (
                        <Skeleton key={index} height={32} width={40} className="rounded-md" />
                      ))}
                    </div>
                    <Skeleton height={32} width={50} className="rounded-md" />
                  </div>
                </div> */}
              </>
            ) : (
              <>
                {viewMode === 'table' ? (
                  <>
                    {/* Table Header */}
                    <div className="bg-gray-50 border-b border-gray-200">
                      <div className="flex py-4 px-6 text-sm font-semibold text-gray-700">
                        <div className="w-[20%]">#</div>
                        <div className="w-[45%]">{t('menuCategory_name')}</div>
                        <div className="w-[35%]">{t('Action')}</div>
                      </div>
                    </div>

                    {/* Table Body */}
                    <div>
                      {currentItems && currentItems.length > 0 ? (
                        <>
                          <div className="flex items-center gap-2 px-6 py-2 bg-amber-50 text-amber-800 text-xs border-b border-amber-200">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 10h16M4 14h10" />
                            </svg>
                            <span className="font-medium">{t('drag_to_reorder')}</span>
                            {isOrderSaving && (
                              <svg className="w-4 h-4 animate-spin text-amber-700" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                              </svg>
                            )}
                          </div>
                          <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="category-table">
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`${isOrderSaving ? "pointer-events-none opacity-70" : ""}`}
                                >
                                  {currentItems.map((item, index) => (
                                    <Draggable
                                      key={item?._id || `${startIndex + index}`}
                                      draggableId={item?._id ? String(item._id) : `category-${startIndex + index}`}
                                      index={index}
                                    >
                                      {(provided, snapshot) => (
                                        <TableRow
                                          item={item}
                                          index={index}
                                          dragHandleProps={provided.dragHandleProps}
                                          draggableProps={provided.draggableProps}
                                          innerRef={provided.innerRef}
                                          isDragging={snapshot.isDragging}
                                        />
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </>
                      ) : (
                        <NoDataFound text='No categories found' desc={searchText ? 'Try adjusting your search terms' : 'Get started by adding your first category'} icon={<NDCategorieI />} showButton={!searchText} buttonText='Add Category' handleClick={handleOpenPopup} />
                      )}
                    </div>
                  </>
                ) : (
                  /* Grid View */
                  <div className="p-6">
                    {currentItems && currentItems.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {currentItems.map((item, index) => (
                          <CategoryCard key={index} item={item} index={index} />
                        ))}
                      </div>
                    ) : (
                      <NoDataFound text='No categories found' desc={searchText ? 'Try adjusting your search terms' : 'Get started by adding your first category'} icon={<NDCategorieI />} showButton={!searchText} buttonText='Add Category' handleClick={handleOpenPopup} />
                    )}
                  </div>
                )}

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageNumbers={pageNumbers}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  onPageChange={onPageChange}
                  onItemsPerPageChange={onItemsPerPageChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Popups */}
      <AddCategory isOpen={isPopupOpen} onClose={handleClosePopup} isEdit={isEdit} isEditItem={isEditItem} />

      <DeleteModal
        isOpen={isOpenDelete}
        onClose={(isDelete) => onDelete(isDelete)}
        title={t('del_C_title')}
        description={t('del_C_desc')}
        item={isDeleteCategory}
        deletebtnTitle={t('btnDelete')}
        closebtnTitle={t('btnClose')}
      />
    </>
  );
};

export default Category;
